import { FC, useMemo, useState, useRef } from "react";
import { BaseDateDataChart } from "../../../../../components/charts/BaseDateDataChart/BaseDateDataChart";
import { LineChartColors } from "../../../../../components/charts/constants";
import { useGetChartData } from "./hooks/useGetChartData";
import { ChartWrapper } from "../../../../../components/ChartWrapper";
import { hasNoData, determineChartContent } from "../../../utils";
import { PeriodSelector } from "../../../../../components/PeriodSelector";
import { useCombinedFilters } from "../../../../../hooks/useCombinedFilters";
import * as am4charts from "@amcharts/amcharts4/charts";
import { useWorkspaceStore } from "../../../../../store/workspace/WorkspaceStore";
import { CHART_CONSTANTS } from "../../constants";

const { baseId, tooltip, chartTitle } =
  CHART_CONSTANTS.keyPlayerMarketPenetrationTrend;
interface Props {
  isLoading: boolean;
  topProductIds: number[];
}

export const KeyPlayerMarketPenetrationTrendChart: FC<Props> = ({
  isLoading,
  topProductIds,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState("1");
  const chartRef = useRef<am4charts.XYChart>(null);

  const combinedFilters = useCombinedFilters();
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );

  const { loading: chartDataLoading, data } = useGetChartData({
    companyFilters: combinedFilters ?? {},
    productIds: topProductIds,
    localizeInstalls,
    lookbackYears: Number(selectedPeriod),
  });

  const loading = isLoading || chartDataLoading;

  const noDataReturned = useMemo(
    () => hasNoData(loading, data),
    [data, loading]
  );

  const chartContent = useMemo(() => {
    return determineChartContent({
      isLoading: loading,
      noDataReturned,
      baseId,
      chartToRender: (
        <BaseDateDataChart
          id={`${baseId}-chart`}
          chartData={data}
          inputDateFormat="yyyy-MM-dd"
          baseColor={LineChartColors}
          showPercentageSymbol={true}
          yAxisLabel="Market Penetration (%)"
          showCustomTooltip={true}
          ref={chartRef}
        />
      ),
    });
  }, [data, loading, noDataReturned]);

  const csvFieldMappings = useMemo(() => {
    const mappings: Record<string, string> = {
      date: "Date",
    };

    if (!data || loading || data.length === 0 || !data[0].series) {
      return mappings;
    }

    const [firstDataPoint] = data;
    firstDataPoint.series.forEach((seriesItem) => {
      const baseKey = seriesItem.name.toLowerCase().replace(/[\s&]+/g, "_");
      mappings[`series_${baseKey}`] = `${seriesItem.name} (%)`;
      mappings[`series_${baseKey}_count`] =
        `${seriesItem.name} (# of companies)`;
    });

    return mappings;
  }, [data, loading]);

  return (
    <ChartWrapper
      id={`${baseId}-chart-wrapper`}
      title={chartTitle}
      tooltipTitle={tooltip}
      chart={chartContent}
      noData={noDataReturned}
      chartRef={chartRef}
      isLoading={loading}
      titleExtras={
        <PeriodSelector
          id={`${baseId}-period-select`}
          selectedPeriod={selectedPeriod}
          onChange={setSelectedPeriod}
        />
      }
      csvFieldMappings={csvFieldMappings}
    />
  );
};
